import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useIsTransport } from '../hooks/useIsTransport';
import { reportsApi } from '../api/reports';
import { invoicesApi } from '../api/invoices';
import { expensesApi } from '../api/expenses';
import { purchasesApi } from '../api/purchases';
import { productsApi } from '../api/products';
import { fmt, fmt2, STATUS_BADGE } from '../lib/utils';
import type { Invoice } from '@coreledger/shared-types';

function KpiCard({ label, value, sub, color, icon, bg }: { label: string; value: string; sub?: string; color: string; icon: string; bg: string }) {
  return (
    <div className="card" style={{ padding: '1.25rem', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color, borderRadius: '10px 10px 0 0' }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>{label}</div>
          <div style={{ fontSize: '1.65rem', fontWeight: 800, color, lineHeight: 1.1, letterSpacing: '-0.02em' }}>{value}</div>
          {sub && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.35rem' }}>{sub}</div>}
        </div>
        <div style={{ width: 42, height: 42, borderRadius: '10px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>{icon}</div>
      </div>
    </div>
  );
}

// Financial year start/end for the current FY
function currentFyRange(): { from: string; to: string } {
  const now = new Date();
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return { from: `${y}-04-01`, to: `${y + 1}-03-31` };
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const isTransport = useIsTransport();
  const fy = currentFyRange();

  // Aggregated totals + monthly trend from the server — no large data transfer
  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ['sales-summary', fy.from, fy.to],
    queryFn: () => reportsApi.salesSummary(fy.from, fy.to),
    staleTime: 0,
  });

  // Outstanding report — gives top customers and overdue breakdown
  const { data: outstanding } = useQuery({
    queryKey: ['outstanding'],
    queryFn: () => reportsApi.outstanding(),
    staleTime: 0,
  });

  // Recent invoices — small, bounded fetch; exclude credit/debit notes (they are not receivables)
  const { data: recentResult } = useQuery({
    queryKey: ['invoices', 'recent'],
    queryFn: () => invoicesApi.list({}, 1, 12),
    staleTime: 30 * 1000,
  });
  const recentInvoices = (recentResult?.data ?? [])
    .filter(i => i.docType !== 'credit_note' && i.docType !== 'debit_note')
    .slice(0, 8);

  // Overdue invoice COUNT — meta.total is the server-side total across all pages
  const { data: overdueResult } = useQuery({
    queryKey: ['invoices', 'overdue'],
    queryFn: () => invoicesApi.list({ status: 'overdue' }, 1, 1),
    staleTime: 0,
  });
  const overdueCount = overdueResult?.meta?.total ?? 0;
  // Overdue AMOUNT from the outstanding ageing report (already fetched, covers ALL invoices)
  // Slice off bucket[0] which is "Current / not yet due"; remaining buckets are past-due
  const overdueAmount = outstanding
    ? outstanding.summary.slice(1).reduce((s, b) => s + b.outstanding, 0)
    : 0;

  // Products — bounded to 100 for low-stock check
  const { data: productsResult } = useQuery({
    queryKey: ['products'],
    queryFn: () => productsApi.list(undefined, 1, 100),
    staleTime: 30 * 1000,
  });
  const products = productsResult?.data ?? [];
  const lowStock = products.filter(p => p.type === 'product' && (p.reorderLevel ?? 0) > 0 && (p.stock ?? 0) <= (p.reorderLevel ?? 0));

  // Expenses — bounded to 100 for category breakdown
  const { data: expensesResult } = useQuery({
    queryKey: ['expenses'],
    queryFn: () => expensesApi.list(1, 100),
    staleTime: 30 * 1000,
  });
  const expenses = expensesResult?.data ?? [];
  const totalExpenses = expenses.reduce((s, e) => s + e.amount, 0);

  // Purchases for COGS — fetch all confirmed PIs for the current FY
  const { data: purchasesResult } = useQuery({
    queryKey: ['purchases', 'dashboard', fy.from, fy.to],
    queryFn: () => purchasesApi.list(1, 500),
    staleTime: 30 * 1000,
  });
  const fyPurchases = (purchasesResult?.data ?? []).filter(
    p => p.date >= fy.from && p.date <= fy.to && p.status === 'confirmed',
  );
  const cogs = fyPurchases.filter(p => p.docType !== 'debit_note').reduce((s, p) => s + p.subtotal, 0)
             - fyPurchases.filter(p => p.docType === 'debit_note').reduce((s, p) => s + p.subtotal, 0);

  const expenseByCategory: Record<string, number> = {};
  for (const e of expenses) {
    const cat = e.category || 'Uncategorized';
    expenseByCategory[cat] = (expenseByCategory[cat] ?? 0) + e.amount;
  }
  const topExpenseCategories = Object.entries(expenseByCategory).sort((a, b) => b[1] - a[1]).slice(0, 6);

  // Top 5 customers from outstanding report
  const topParties = [...(outstanding?.parties ?? [])]
    .sort((a, b) => b.totalInvoiced - a.totalInvoiced)
    .slice(0, 5);

  // Monthly revenue from summary
  const monthlyRevenue = summary?.byMonth?.slice(-6) ?? [];
  const maxMonthly = Math.max(...monthlyRevenue.map(m => m.total), 1);

  const revenue = summary?.totals.subtotal ?? 0;
  const outstanding_amount = summary?.totals.outstanding ?? 0;
  const invoiceCount = summary?.totals.invoiceCount ?? 0;
  const quotations = summary?.quotations;

  const isLoading = loadingSummary;
  if (isLoading) return <div className="loading-overlay"><span className="spinner" /></div>;

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
      </div>

      {/* Low stock alert */}
      {lowStock.length > 0 && (
        <div className="warn-box" style={{ marginBottom: '1rem' }}>
          <span>⚠️</span>
          <span style={{ fontWeight: 500 }}>
            <strong>{lowStock.length} product{lowStock.length > 1 ? 's' : ''}</strong> below reorder level:{' '}
            {lowStock.slice(0, 3).map(p => p.name).join(', ')}{lowStock.length > 3 ? '…' : ''}
          </span>
          <a href="/products" style={{ marginLeft: 'auto', fontWeight: 600, fontSize: '0.8rem' }}>View →</a>
        </div>
      )}

      {/* KPI Cards */}
      <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
        <KpiCard label="Total Revenue" value={fmt(revenue)} sub={`${invoiceCount} invoices`} color="var(--primary)" icon="💰" bg="var(--primary-light)" />
        <KpiCard label="Outstanding" value={fmt(outstanding_amount)} sub="receivables" color="var(--warning)" icon="⏳" bg="#fffbeb" />
        <KpiCard label="Overdue Count" value={String(overdueCount)} sub="invoices" color="var(--danger)" icon="🚨" bg="#fff1f2" />
        <KpiCard label="Overdue Amount" value={fmt(overdueAmount)} sub={`${overdueCount} invoice${overdueCount !== 1 ? 's' : ''}`} color="#dc2626" icon="🔴" bg="#fff1f2" />
        <KpiCard label="Total Expenses" value={fmt(totalExpenses)} sub={`${expenses.length} entries`} color="var(--text-2)" icon="📉" bg="var(--surface-alt)" />
        {quotations && quotations.total > 0 && (
          <div style={{ background: '#f5f3ff', borderRadius: 12, padding: '0.9rem 1.1rem', border: '1px solid #ede9fe', gridColumn: 'span 1' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '1.1rem' }}>📋</span>
              <span style={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#7c3aed' }}>Quotations</span>
            </div>
            <div style={{ fontWeight: 800, fontSize: '1.3rem', color: '#7c3aed', marginBottom: '0.3rem' }}>{fmt(quotations.pipelineValue)}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-2)', lineHeight: 1.7 }}>
              <span style={{ color: 'var(--text-muted)' }}>{quotations.pending} pending</span>
              {quotations.won > 0 && <span style={{ color: '#16a34a', marginLeft: '0.5rem' }}>✓ {quotations.won} won ({fmt(quotations.wonValue)})</span>}
              {quotations.lost > 0 && <span style={{ color: '#dc2626', marginLeft: '0.5rem' }}>✗ {quotations.lost} lost</span>}
              {(quotations.won + quotations.lost) > 0 && <span style={{ marginLeft: '0.5rem', fontWeight: 600, color: '#7c3aed' }}>Win rate: {quotations.winRate}%</span>}
            </div>
          </div>
        )}
      </div>

      {/* Revenue Trend */}
      {monthlyRevenue.length > 0 && (
        <div className="card" style={{ marginBottom: '1.25rem', padding: '1rem 1.25rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1rem' }}>Revenue — Last 6 Months</div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', height: 80 }}>
            {monthlyRevenue.map(m => (
              <div key={m.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--primary)', whiteSpace: 'nowrap' }}>
                  {m.total > 0 ? (m.total >= 100000 ? `₹${(m.total / 100000).toFixed(1)}L` : `₹${(m.total / 1000).toFixed(0)}K`) : ''}
                </div>
                <div style={{
                  width: '100%', borderRadius: '4px 4px 0 0',
                  background: m.total > 0 ? 'var(--primary)' : 'var(--border)',
                  height: m.total > 0 ? `${Math.max(6, Math.round((m.total / maxMonthly) * 56))}px` : '4px',
                  transition: 'height 0.3s ease',
                }} title={`${m.month}: ₹${m.total.toLocaleString('en-IN')}`} />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {new Date(m.month + '-01').toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="card" style={{ marginBottom: '1.25rem', padding: '1rem 1.25rem' }}>
        <div style={{ fontWeight: 700, fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.85rem' }}>Quick Actions</div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          {[
            { label: '🧾 New Invoice',  action: () => navigate('/invoices/new'),        primary: true },
            ...(isTransport ? [{ label: '🚚 New LR', action: () => navigate('/lorry-receipts/new') }] : []),
            { label: '🏢 New Party',    action: () => navigate('/parties?new=1') },
            { label: '📦 New Product',  action: () => navigate('/products') },
            { label: '🛒 Purchase',     action: () => navigate('/purchases') },
            { label: '💸 Expense',      action: () => navigate('/expenses') },
          ].map(({ label, action, primary }) => (
            <button key={label} className={primary ? 'btn btn-primary' : 'btn btn-ghost'} onClick={action}>{label}</button>
          ))}
        </div>
      </div>

      <div className="dash-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '1.25rem' }}>
        {/* Recent Invoices */}
        <div className="card">
          <div className="card-header">
            <span style={{ fontWeight: 700 }}>Recent Invoices</span>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/invoices')}>View all →</button>
          </div>
          {recentInvoices.length === 0 ? (
            <div className="empty-state" style={{ padding: '1.5rem' }}>
              <div className="empty-state-icon">🧾</div>
              <div className="empty-state-text">No invoices yet</div>
            </div>
          ) : (
            <table>
              <thead>
                <tr><th>Invoice #</th><th>Date</th><th>Party</th><th style={{ textAlign: 'right' }}>Amount</th><th>Status</th></tr>
              </thead>
              <tbody>
                {recentInvoices.map((inv: Invoice) => (
                  <tr key={inv.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/invoices/${inv.id}`)}>
                    <td style={{ fontWeight: 600, color: 'var(--primary)' }}>{inv.invoiceNumber || '(draft)'}</td>
                    <td style={{ color: 'var(--text-2)' }}>{inv.date}</td>
                    <td>{inv.partyName}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(inv.total)}</td>
                    <td><span className={`badge ${STATUS_BADGE[inv.status] ?? 'badge-gray'}`}>{inv.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Top customers */}
          <div className="card">
            <div className="card-header"><span style={{ fontWeight: 700 }}>Top Customers</span></div>
            {topParties.length === 0 ? (
              <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.875rem', textAlign: 'center' }}>No sales yet</div>
            ) : (
              <div style={{ padding: '0.25rem 0' }}>
                {topParties.map((p, i) => (
                  <div key={p.partyId} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1.25rem', borderBottom: i < topParties.length - 1 ? '1px solid var(--border)' : undefined }}>
                    <div style={{ width: 24, height: 24, background: 'var(--primary-light)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 800, color: 'var(--primary)', flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.partyName}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{p.invoiceCount} invoice{p.invoiceCount !== 1 ? 's' : ''}</div>
                    </div>
                    <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.875rem', flexShrink: 0 }}>{fmt(p.totalInvoiced)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Expense breakdown */}
          {topExpenseCategories.length > 0 && (
            <div className="card">
              <div className="card-header"><span style={{ fontWeight: 700 }}>Expenses by Category</span></div>
              <div style={{ padding: '0.25rem 0' }}>
                {topExpenseCategories.map(([cat, amt], i) => {
                  const pct = totalExpenses > 0 ? Math.round((amt / totalExpenses) * 100) : 0;
                  return (
                    <div key={cat} style={{ padding: '0.5rem 1.25rem', borderBottom: i < topExpenseCategories.length - 1 ? '1px solid var(--border)' : undefined }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <span style={{ fontSize: '0.825rem', fontWeight: 500 }}>{cat}</span>
                        <span style={{ fontSize: '0.825rem', fontWeight: 700, color: 'var(--danger)' }}>{fmt(amt)}</span>
                      </div>
                      <div style={{ height: 4, background: 'var(--border)', borderRadius: 2 }}>
                        <div style={{ height: 4, width: `${pct}%`, background: 'var(--danger)', borderRadius: 2, opacity: 0.6 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* P&L snapshot */}
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.75rem' }}>P&L Snapshot</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-2)' }}>Net Revenue</span>
              <span style={{ fontWeight: 600, color: 'var(--success)' }}>{fmt2(revenue)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-2)' }}>Purchase Cost (COGS)</span>
              <span style={{ fontWeight: 600, color: cogs > 0 ? 'var(--danger)' : 'var(--text-2)' }}>{cogs > 0 ? `−${fmt2(cogs)}` : fmt2(0)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-2)' }}>Expenses</span>
              <span style={{ fontWeight: 600, color: totalExpenses > 0 ? 'var(--danger)' : 'var(--text-2)' }}>{totalExpenses > 0 ? `−${fmt2(totalExpenses)}` : fmt2(0)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid var(--border)', paddingTop: '0.4rem', marginTop: '0.25rem' }}>
              <span style={{ fontWeight: 700 }}>Net Profit / (Loss)</span>
              <span style={{ fontWeight: 800, color: revenue - cogs - totalExpenses >= 0 ? 'var(--success)' : 'var(--danger)' }}>{fmt2(revenue - cogs - totalExpenses)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
